import "./StaffForm.scss";
import InfoContext from "pages/InfoContext";
import { FormattedMessage } from "react-intl";
import { INPUTS_IDS, LANG, RECRUITMENT_METHOD } from "constants/constants";
import {
  STAFF_FORM_LABELS,
  STAFF_FORM_PLACEHOLDERS,
  SCHOOL_LEVEL_OPTIONS,
} from "constants/forms";
import { Typography } from "core/typography/Typography";
import { useContext, useEffect, useState, useMemo } from "react";
import {
  renderBirthday,
  renderFirstName,
  renderInputAddress,
  renderInputBirthtown,
  renderInputCooptedBy,
  renderInputDegree,
  renderInputEmail,
  renderInputGender,
  renderInputNationality,
  renderInputPicture,
  renderInputRecruitmentMode,
  renderRefererExternal,
  renderInputYear,
  renderLastName,
  renderPhonenumber,
  renderSchool,
  renderSchoolLevel,
} from "../CommonInputs";
import propTypes from "prop-types";

const PersonalInfoForm = ({
  control,
  errors,
  readOnly,
  register,
  setValue,
  watch,
  unregister,
}) => {
  const {
    lang,
    personsByProfileList,
    isMobile,
    getOptions,
    recruitmentMethodList,
    id,
    cellHighlight,
    nationalities,
    picture,
    createFile,
    uploadProfilePicture,
    createdFile,
    setCreatedFile,
    setStatus,
    setCode,
    apiErrorResponse,
    tempUserId,
    slug,
  } = useContext(InfoContext);

  const [recruitmentMethods, setRecruitmentMethods] = useState([]);
  const recruitment_mode_id = watch("recruitment_mode_id", []);
  const referer_internal_id = watch("referer_internal_id", []);
  const gender = watch("gender", "");
  const nationality = watch("nationality", []);

  // Memoize picture value to prevent unnecessary re-renders
  const pictureValue = useMemo(() => {
    return picture || createdFile?.uri || createdFile?.picture_file_id || "";
  }, [picture, createdFile?.uri, createdFile?.picture_file_id]);

  useEffect(() => {
    if (recruitmentMethodList.length > 0 && lang) {
      setRecruitmentMethods(
        recruitmentMethodList
          ?.filter((e) => e.invisible === false)
          .map((elm) => ({
            label: lang === LANG.EN ? elm.en : elm.fr,
            value: elm.id,
            type: "recruitment_mode",
          }))
      );
    }
  }, [lang, recruitmentMethodList]);

  useEffect(() => {
    setValue(
      "picture_file_id",
      createdFile?.picture_file_id || createdFile?.uri
    );
  }, [createdFile]);

  useEffect(() => {
    if (recruitment_mode_id[0]?.value === RECRUITMENT_METHOD.REFERER_EXTERNAL) {
      unregister("referer_internal_id");
    } else if (
      recruitment_mode_id[0]?.value === RECRUITMENT_METHOD.REFERER_INTERNAL
    ) {
      unregister("referer_external");
    } else {
      unregister("referer_internal_id");
      unregister("referer_external");
    }
  }, [recruitment_mode_id]);

  const displayInputAddress = () => {
    return (
      <div
        className={`corner-right ${
          (!watch("address") && cellHighlight) ||
          errors?.["address"] ||
          apiErrorResponse?.["address"]
            ? "errorCellColor"
            : ""
        }`}
      >
        {renderInputAddress({
          errorMessage:
            apiErrorResponse?.["address"] || errors?.["address"]?.message,
          control,
          errors,
          label: STAFF_FORM_LABELS.address,
          placeholder: STAFF_FORM_PLACEHOLDERS.address,
          readOnly,
          register,
          id: STAFF_FORM_LABELS.address.id,
        })}
      </div>
    );
  };

  const displayInputPhoneNumber = () => {
    return (
      <div
        className={`cell-padding ${
          (!watch("phone_number") && cellHighlight) ||
          errors?.["phone_number"] ||
          apiErrorResponse?.["phone_number"]
            ? "errorCellColor"
            : ""
        }`}
      >
        {renderPhonenumber({
          control,
          errors,
          label: STAFF_FORM_LABELS.phone_number,
          placeholder: STAFF_FORM_PLACEHOLDERS.phone_number,
          readOnly,
          register,
          required: true,
          errorMessage:
            apiErrorResponse?.["phone_number"] ||
            (watch("phone_number") && errors?.["phone_number"]?.message),
        })}
      </div>
    );
  };

  const formFieldsLine1 = [
    {
      render: renderInputGender,
      label: STAFF_FORM_LABELS.gender,
      placeholder: STAFF_FORM_PLACEHOLDERS.placeholder,
      value: gender,
      setValue,
      id: "gender",
    },
    {
      render: renderLastName,
      label: STAFF_FORM_LABELS.last_name,
      placeholder: STAFF_FORM_PLACEHOLDERS.last_name,
      id: "last_name",
      errorMessage: apiErrorResponse?.["last_name"],
    },
    {
      render: renderFirstName,
      label: STAFF_FORM_LABELS.first_name,
      placeholder: STAFF_FORM_PLACEHOLDERS.first_name,
      id: "first_name",
      errorMessage: apiErrorResponse?.["first_name"],
    },
    {
      render: renderBirthday,
      label: STAFF_FORM_LABELS.birthday,
      placeholder: STAFF_FORM_PLACEHOLDERS.birthday,
      lang,
      id: "birthday",
    },
    {
      render: renderInputEmail,
      label: STAFF_FORM_LABELS.personal_email,
      placeholder: STAFF_FORM_PLACEHOLDERS.personal_email,
      name: "personal_email",
      id: "personal_email",
      errorMessage:
        apiErrorResponse?.["personal_email"] ||
        apiErrorResponse?.["email"] ||
        (watch("personal_email") && errors?.["personal_email"]?.message),
      copyEmail: slug,
      setCode,
      setStatus,
    },
  ];

  const renderLine1 = () => (
    <>
      {formFieldsLine1.map((field) => (
        <div
          className={`cell-padding ${
            (!watch(field?.id) && cellHighlight) ||
            errors?.[field?.id] ||
            apiErrorResponse?.[field?.id]
              ? "errorCellColor"
              : ""
          }`}
          key={field.id}
        >
          {field.render({
            control,
            errors,
            label: field.label,
            placeholder: field.placeholder,
            readOnly,
            register,
            setValue,
            value: watch(field.id),
            ...field,
          })}
        </div>
      ))}
      {isMobile && displayInputAddress()}
    </>
  );

  const formFieldsLine2 = [
    {
      render: renderInputBirthtown,
      label: STAFF_FORM_LABELS.birth_place,
      placeholder: STAFF_FORM_PLACEHOLDERS.birth_place,
      id: "birth_place",
      errorMessage: apiErrorResponse?.["birth_place"],
    },
    {
      render: renderInputNationality,
      label: STAFF_FORM_LABELS.nationality_id,
      placeholder: STAFF_FORM_PLACEHOLDERS.nationality,
      value: nationality,
      nationalities,
      id: "nationality",
      lang,
      name: "nationality",
      errors,
      setValue,
      errorMessage:
        apiErrorResponse?.["nationality"] || errors?.["nationality"]?.message,
    },
    {
      render: renderSchool,
      label: STAFF_FORM_LABELS.school,
      placeholder: STAFF_FORM_PLACEHOLDERS.school,
      id: "school",
      errorMessage: apiErrorResponse?.["school"] || errors?.["school"]?.message,
    },
    {
      render: renderSchoolLevel,
      label: STAFF_FORM_LABELS.school_level,
      placeholder: STAFF_FORM_PLACEHOLDERS.school_level,
      setValue,
      options: SCHOOL_LEVEL_OPTIONS,
      id: "school_level",
    },
    {
      render: renderInputDegree,
      label: STAFF_FORM_LABELS.degree,
      placeholder: STAFF_FORM_PLACEHOLDERS.degree,
      id: "degree",
      errorMessage: apiErrorResponse?.["degree"] || errors?.["degree"]?.message,
    },
    {
      render: renderInputYear,
      label: STAFF_FORM_LABELS.degree_year,
      placeholder: STAFF_FORM_PLACEHOLDERS.degree_year,
      className: "year",
      id: "degree_year",
    },
  ];

  const renderLine2 = () => (
    <>
      {formFieldsLine2?.map((field) => {
        return (
          <div
            className={`cell-padding ${
              (!watch(field?.id) && cellHighlight) ||
              errors?.[field?.id] ||
              apiErrorResponse?.[field?.id]
                ? "errorCellColor"
                : ""
            } ${field?.id === "degree_year" ? "year" : ""}`}
            key={field.id}
          >
            {field.render({
              control,
              errors,
              label: field.label,
              placeholder: field.placeholder,
              readOnly,
              register,
              value: watch(field.id),
              setValue,
              ...field,
            })}
          </div>
        );
      })}
      {isMobile && displayInputPhoneNumber()}
    </>
  );

  useEffect(() => {
    if (recruitment_mode_id?.length > 0) {
      switch (recruitment_mode_id[0]?.value) {
        case RECRUITMENT_METHOD.REFERER_EXTERNAL:
          setValue("referer_internal_id", "");
          break;
        case RECRUITMENT_METHOD.REFERER_INTERNAL:
          setValue("referer_external", "");
          break;
        default:
          setValue("referer_internal_id", "");
          setValue("referer_external", "");
          break;
      }
    }
  }, [recruitment_mode_id]);

  return (
    <div className="section-1">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage id="who_s_she_he" defaultMessage="WHO'S SHE/HE ?" />
        }
      />
      {id && (
        <div className="staff-personal-info-form" style={{ width: "100%" }}>
          <div className="center-block">
            <div className="line-1-personal">{renderLine1()}</div>
            <div className="line-2-personal">{renderLine2()}</div>
          </div>
          {!isMobile && displayInputAddress()}
        </div>
      )}
      {!id && (
        <div className="staff-personal-info-form">
          <div className="corner-left">
            {renderInputPicture({
              readOnly,
              label: STAFF_FORM_LABELS.picture_file_id,
              value: pictureValue,
              createFile,
              uploadProfilePicture,
              setCreatedFile,
              id: INPUTS_IDS.STAFF,
              setCode,
              setStatus,
              userId: tempUserId,
              createdFile,
            })}
          </div>
          <div className="center-block">
            <div className="line-1">{renderLine1()}</div>
            <div className="line-2">{renderLine2()}</div>
          </div>
          {!isMobile && displayInputAddress()}
        </div>
      )}
      <div className="staff-personal-info-form-last-block">
        {!isMobile && displayInputPhoneNumber()}
        <div className="cell-padding">
          {renderInputRecruitmentMode({
            control,
            errors,
            label: STAFF_FORM_LABELS.recruitment_mode_id,
            placeholder: STAFF_FORM_PLACEHOLDERS.recruitment_mode_id,
            readOnly,
            recruitmentMethods,
            required: false,
            register,
            setValue,
            value: recruitment_mode_id,
          })}
        </div>
        {recruitment_mode_id &&
          recruitment_mode_id[0]?.value ===
            RECRUITMENT_METHOD.REFERER_INTERNAL && (
            <div
              className={`cell-padding ${
                !watch("referal_internal_id") &&
                cellHighlight &&
                "errorCellColor"
              }`}
            >
              {renderInputCooptedBy({
                control,
                errors,
                getOptions,
                label: STAFF_FORM_LABELS.referer_internal_id,
                placeholder: STAFF_FORM_PLACEHOLDERS.referer_internal_id,
                readOnly,
                recruitment_mode_id,
                register,
                setValue,
                value: referer_internal_id,
                personsList: personsByProfileList,
              })}
            </div>
          )}
        {recruitment_mode_id &&
          recruitment_mode_id[0]?.value ===
            RECRUITMENT_METHOD.REFERER_EXTERNAL && (
            <div
              className={`cell-padding ${
                !watch("referal_external") && cellHighlight && "errorCellColor"
              }`}
            >
              {renderRefererExternal({
                control,
                label: STAFF_FORM_LABELS.referer_external,
                placeholder: STAFF_FORM_PLACEHOLDERS.referer_external,
                readOnly,
                register,
                errors,
              })}
            </div>
          )}
      </div>
    </div>
  );
};

export default PersonalInfoForm;

PersonalInfoForm.propTypes = {
  control: propTypes.object,
  errors: propTypes.object,
  readOnly: propTypes.bool,
  register: propTypes.func,
  setValue: propTypes.func,
  watch: propTypes.func,
  unregister: propTypes.func,
};
